import React, { useState, useEffect } from 'react';
import { trainingGuideAPI } from '../utils/api';

const TrainingGuidesPage = () => {
    const [guides, setGuides] = useState([]);
    const [selectedGuide, setSelectedGuide] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPublishedGuides = async () => {
            try {
                const response = await trainingGuideAPI.getAll();
                setGuides(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Error fetching guides:', error);
                setGuides([]);
            } finally {
                setLoading(false);
            }
        };
        fetchPublishedGuides();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen"
                style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 100%)' }}>
                <div className="text-center">
                    <div className="w-12 h-12 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-slate-400 font-light">Loading guides…</p>
                </div>
            </div>
        );
    }

    if (selectedGuide) {
        return (
            <div className="overflow-x-hidden">
                {/* Guide hero */}
                <section
                    className="relative pt-36 pb-16 overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 60%, #1e1b4b 100%)' }}
                >
                    <div className="absolute top-1/2 left-1/4 w-72 h-72 rounded-full opacity-15 pointer-events-none"
                        style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)', transform: 'translateY(-50%)' }} />
                    <div className="relative max-w-4xl mx-auto px-6">
                        <button
                            onClick={() => setSelectedGuide(null)}
                            className="inline-flex items-center gap-2 text-slate-400 hover:text-white font-medium text-sm mb-8 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                            </svg>
                            Back to Guides
                        </button>
                        <div className="inline-flex items-center gap-2 border border-blue-500/40 rounded-full px-4 py-1.5 text-blue-300 text-xs font-medium mb-5"
                            style={{ background: 'rgba(99,102,241,0.12)' }}>
                            CLINICAL GUIDE
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 leading-tight">{selectedGuide.title}</h1>
                        <p className="text-slate-300 font-light text-lg max-w-2xl">{selectedGuide.description}</p>
                        <div className="flex items-center gap-2 mt-5 text-slate-500 text-sm font-light">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {selectedGuide.views} views
                        </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                            <path d="M0 40L720 10L1440 40V0H0Z" fill="#f8fafc" />
                        </svg>
                    </div>
                </section>

                <div className="bg-slate-50 pb-24">
                    <div className="max-w-4xl mx-auto px-6 pt-10">
                        {selectedGuide.thumbnail && (
                            <img src={selectedGuide.thumbnail} alt={selectedGuide.title}
                                className="w-full h-80 object-cover rounded-3xl mb-10 shadow-xl" />
                        )}
                        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-100 shadow-sm">
                            <div
                                dangerouslySetInnerHTML={{ __html: selectedGuide.content || '<p>No content available</p>' }}
                                className="prose prose-slate prose-lg max-w-none"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="overflow-x-hidden">
            {/* ── HERO ──────────────────────────────────────────── */}
            <section
                className="relative pt-36 pb-20 overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 50%, #1e1b4b 100%)' }}
            >
                <div className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full opacity-15 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)', transform: 'translateY(-50%)' }} />
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
                <div className="relative max-w-7xl mx-auto px-6">
                    <div className="inline-flex items-center gap-2 border border-indigo-500/40 rounded-full px-4 py-1.5 text-indigo-300 text-xs font-medium tracking-widest mb-6"
                        style={{ background: 'rgba(99,102,241,0.12)', backdropFilter: 'blur(8px)' }}>
                        <span className="w-2 h-2 rounded-full bg-indigo-400" />
                        CLINICAL RESOURCES
                    </div>
                    <h1 className="text-6xl sm:text-7xl font-bold text-white mb-4 tracking-tight">Training Guides</h1>
                    <p className="text-xl text-slate-300 font-light max-w-xl leading-relaxed">
                        Expert techniques for medical support and therapeutic taping — written by clinicians.
                    </p>
                </div>
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 50L80 43C160 36 320 22 480 16C640 10 800 16 960 22C1120 28 1280 34 1360 37L1440 40V0H0Z" fill="#f8fafc" />
                    </svg>
                </div>
            </section>

            <div className="bg-slate-50 pb-24">
                <div className="max-w-7xl mx-auto px-6 pt-12">
                    {guides.length === 0 ? (
                        <div className="text-center py-24">
                            <div className="w-20 h-20 rounded-3xl bg-white border border-slate-100 flex items-center justify-center mx-auto mb-6">
                                <svg className="w-9 h-9 text-slate-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                                </svg>
                            </div>
                            <p className="text-slate-500 font-light text-lg">No training guides available yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {guides.map(guide => (
                                <div
                                    key={guide._id}
                                    onClick={() => setSelectedGuide(guide)}
                                    className="group bg-white border border-slate-100 hover:border-indigo-200 rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-indigo-100 cursor-pointer"
                                >
                                    {guide.thumbnail ? (
                                        <div className="relative overflow-hidden aspect-video">
                                            <img src={guide.thumbnail} alt={guide.title}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        </div>
                                    ) : (
                                        <div className="aspect-video bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center">
                                            <svg className="w-12 h-12 text-indigo-300" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                                            </svg>
                                        </div>
                                    )}
                                    <div className="p-6">
                                        <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">{guide.title}</h3>
                                        <p className="text-slate-500 font-light text-sm mb-4 line-clamp-2 leading-relaxed">{guide.description}</p>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-1.5 text-xs text-slate-400 font-light">
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                {guide.views} views
                                            </div>
                                            <span className="text-xs font-semibold text-indigo-600 group-hover:gap-2 flex items-center gap-1 transition-all">
                                                Read guide
                                                <svg className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TrainingGuidesPage;
